const { buildCampaignParameters } = require("./campaign-attribution")
const {
  isDoNotTrackActive,
  isMeasurementEnabled,
} = require("./measurement-preferences")
const {
  PAGE_METADATA,
  SITE_URL,
  normalizePathname,
} = require("./seo-foundations")

const OFFICIAL_ADMISSIONS_NUMBER = "5215548885013"
const NOT_FOUND_PATH = PAGE_METADATA.notFound.pathname
const CANONICAL_PAGE_PATHS = new Set(
  Object.values(PAGE_METADATA).map(metadata => metadata.pathname)
)
const SITE_ORIGIN = SITE_URL.replace(/\/+$/, "")

const EVENT_NAMES = Object.freeze({
  WHATSAPP_CLICK: "whatsapp_click",
  PHONE_CLICK: "phone_click",
  DIRECTIONS_CLICK: "directions_click",
  COSTS_CLICK: "costs_click",
  VISIT_INTENT: "visit_intent",
  VISIT_FORM_START: "visit_form_start",
  VISIT_FORM_SUBMIT: "visit_form_submit",
})

function valueOrFallback(value, fallback) {
  if (typeof value === "string" && value.trim()) return value.trim()
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  return fallback
}

function buildWhatsAppUrl(messageOrOptions) {
  const message =
    typeof messageOrOptions === "string"
      ? messageOrOptions
      : messageOrOptions && messageOrOptions.message
  const contextualMessage = valueOrFallback(
    message,
    "Hola, quiero solicitar informes sobre Kalpilli."
  )

  return `https://wa.me/${OFFICIAL_ADMISSIONS_NUMBER}?text=${encodeURIComponent(
    contextualMessage
  )}`
}

function readValue(values, camelCaseKey, snakeCaseKey, fallback) {
  return valueOrFallback(
    values[camelCaseKey] === undefined
      ? values[snakeCaseKey]
      : values[camelCaseKey],
    fallback
  )
}

function buildCanonicalPageIdentifiers(pathname) {
  let normalizedPath = NOT_FOUND_PATH
  try {
    normalizedPath = normalizePathname(
      valueOrFallback(pathname, NOT_FOUND_PATH)
    )
  } catch (_) {
    normalizedPath = NOT_FOUND_PATH
  }

  const pagePath = CANONICAL_PAGE_PATHS.has(normalizedPath)
    ? normalizedPath
    : NOT_FOUND_PATH

  return {
    page_path: pagePath,
    page_location: `${SITE_ORIGIN}${pagePath}`,
  }
}

function buildSafePageReferrer(referrer) {
  if (typeof referrer !== "string" || !referrer.trim()) return ""

  try {
    const parsed = new URL(referrer.trim())
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return ""
    }
    if (parsed.origin === SITE_ORIGIN) {
      return buildCanonicalPageIdentifiers(parsed.pathname).page_location
    }
    return parsed.origin
  } catch (_) {
    return ""
  }
}

function buildAnalyticsPayload(eventName, values = {}, context = {}) {
  const safeValues = values && typeof values === "object" ? values : {}
  const safeContext = context && typeof context === "object" ? context : {}
  const page = buildCanonicalPageIdentifiers(
    readValue(safeValues, "pagePath", "page_path", NOT_FOUND_PATH)
  )
  const campaign = buildCampaignParameters(safeContext.attribution || null)

  switch (eventName) {
    case EVENT_NAMES.WHATSAPP_CLICK:
      return {
        ...page,
        school_level: readValue(
          safeValues,
          "schoolLevel",
          "school_level",
          "general"
        ),
        cta_location: readValue(
          safeValues,
          "ctaLocation",
          "cta_location",
          "unknown"
        ),
        destination_number: readValue(
          safeValues,
          "destinationNumber",
          "destination_number",
          "unknown"
        ),
        ...campaign,
      }

    case EVENT_NAMES.PHONE_CLICK:
    case EVENT_NAMES.COSTS_CLICK:
    case EVENT_NAMES.VISIT_INTENT:
      return {
        ...page,
        school_level: readValue(
          safeValues,
          "schoolLevel",
          "school_level",
          "general"
        ),
        cta_location: readValue(
          safeValues,
          "ctaLocation",
          "cta_location",
          "unknown"
        ),
        ...campaign,
      }

    case EVENT_NAMES.DIRECTIONS_CLICK:
      return {
        ...page,
        campus: readValue(safeValues, "campus", "campus", "unknown"),
        map_provider: readValue(
          safeValues,
          "mapProvider",
          "map_provider",
          "unknown"
        ),
        ...campaign,
      }

    case EVENT_NAMES.VISIT_FORM_START:
    case EVENT_NAMES.VISIT_FORM_SUBMIT:
      return {
        ...page,
        school_level: readValue(
          safeValues,
          "schoolLevel",
          "school_level",
          "general"
        ),
        form_provider: readValue(
          safeValues,
          "formProvider",
          "form_provider",
          "unknown"
        ),
        ...campaign,
      }

    default:
      return null
  }
}

function dispatchAnalyticsEvent(eventName, payload, target) {
  try {
    const analyticsTarget =
      target || (typeof window !== "undefined" ? window : undefined)
    if (
      !analyticsTarget ||
      typeof eventName !== "string" ||
      !eventName.trim() ||
      !payload ||
      typeof payload !== "object" ||
      Array.isArray(payload) ||
      !isMeasurementEnabled(analyticsTarget.__KALPILLI_MEASUREMENT_STATE__) ||
      isDoNotTrackActive(analyticsTarget) ||
      analyticsTarget.__KALPILLI_GA4_INITIALIZED__ !== true ||
      typeof analyticsTarget.gtag !== "function"
    ) {
      return false
    }

    const safePage = buildCanonicalPageIdentifiers(payload.page_path)
    analyticsTarget.gtag("set", safePage)
    analyticsTarget.gtag("event", eventName, {
      ...payload,
      ...safePage,
    })
    return true
  } catch (_) {
    return false
  }
}

function buildPageViewPayload(location = {}, title = "", attribution = null) {
  const safeLocation = location && typeof location === "object" ? location : {}
  const page = buildCanonicalPageIdentifiers(safeLocation.pathname)
  return {
    ...page,
    page_title: valueOrFallback(title, "unknown"),
    ...(attribution ? buildCampaignParameters(attribution) : {}),
  }
}

function dispatchPageView(target, location, title, attribution = null) {
  return dispatchAnalyticsEvent(
    "page_view",
    buildPageViewPayload(location, title, attribution),
    target
  )
}

module.exports = {
  EVENT_NAMES,
  OFFICIAL_ADMISSIONS_NUMBER,
  buildAnalyticsPayload,
  buildCanonicalPageIdentifiers,
  buildPageViewPayload,
  buildSafePageReferrer,
  buildWhatsAppUrl,
  dispatchAnalyticsEvent,
  dispatchPageView,
}
